import React, { useEffect, useState } from "react";
import { Button, Table, Tag, Typography, Alert, Space, Select, Progress } from "antd";
import { SendOutlined, MailOutlined } from "@ant-design/icons";
import StyledModal from "../../components/CustomModal";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { fetchEmailTemplates, fetchNotPaidClaims, sendBulkCustomEmail, NotPaidClaim } from "../../redux/features/emailTemplate/emailTemplateAsyncThunk";
import { selectEmailTemplate } from "../../redux/features/emailTemplate/emailTemplateSlice";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const { Text } = Typography;

interface BulkEmailModalProps {
  open: boolean;
  onClose: () => void;
}

const BulkEmailModal: React.FC<BulkEmailModalProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { data: emailTemplates } = useAppSelector(selectEmailTemplate);
  const [claims, setClaims] = useState<NotPaidClaim[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [progress, setProgress] = useState<{ sent: number; total: number } | null>(null);

  useEffect(() => {
    if (open) {
      setLoading(true);
      setSent(false);
      setSelectedRowKeys([]);
      setSelectedTemplateId(null);
      setProgress(null);
      dispatch(fetchNotPaidClaims())
        .unwrap()
        .then((data) => setClaims(data))
        .catch((err) => toast.error(err || "Failed to fetch claims"))
        .finally(() => setLoading(false));

      // Fetch email templates if not loaded
      if (!emailTemplates || emailTemplates.length === 0) {
        dispatch(fetchEmailTemplates({ status: 1 }));
      }
    }
  }, [open, dispatch]);

  const handleSend = async () => {
    if (!selectedTemplateId) {
      toast.warning("Please select an email template");
      return;
    }
    if (selectedRowKeys.length === 0) {
      toast.warning("Please select at least one claim");
      return;
    }
    setSending(true);
    setProgress({ sent: 0, total: selectedRowKeys.length });
    try {
      await dispatch(sendBulkCustomEmail({
        templateId: selectedTemplateId,
        lostIds: selectedRowKeys as string[],
      })).unwrap();
      setSent(true);
      setProgress({ sent: selectedRowKeys.length, total: selectedRowKeys.length });
    } catch (err: any) {
      toast.error(err || "Failed to send bulk email");
    } finally {
      setSending(false);
    }
  };

  const templateOptions = (emailTemplates || [])
    .filter((t: any) => t.status === 1)
    .map((t: any) => ({
      label: t.longTitle || t.shortTitle,
      value: t.id,
    }));

  const columns = [
    {
      title: "Claim #",
      dataIndex: "trackingId",
      key: "trackingId",
      width: 140,
      render: (text: string) => <Text strong style={{ fontFamily: "monospace" }}>{text}</Text>,
    },
    {
      title: "Customer",
      key: "name",
      render: (_: any, record: NotPaidClaim) => (
        <div>
          <Text strong>{record.firstName} {record.lastName}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text>
        </div>
      ),
    },
    {
      title: "Reported",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date: string) => dayjs(date).format("MM/DD/YYYY"),
    },
    {
      title: "Last Emailed",
      dataIndex: "lastEmailedAt",
      key: "lastEmailedAt",
      width: 130,
      render: (date: string | null) =>
        date ? (
          <Tag color="blue">{dayjs(date).format("MM/DD/YYYY")}</Tag>
        ) : (
          <Tag color="default">Never</Tag>
        ),
    },
  ];

  const selectedCount = selectedRowKeys.length;
  const totalCount = claims.length;
  const selectedTemplateName = templateOptions.find((t: any) => t.value === selectedTemplateId)?.label || "";

  return (
    <StyledModal
      title={
        <Space>
          <MailOutlined />
          <span>Bulk Email — Not Paid Claims</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={800}
      footer={
        <div style={{ padding: "8px 0", width: "100%" }}>
          {progress && (
            <div style={{ marginBottom: 8 }}>
              <Progress
                percent={progress.total > 0 ? Math.round((progress.sent / progress.total) * 100) : 0}
                status={sent ? "success" : sending ? "active" : "normal"}
                size="small"
                format={() => sent ? `${progress.sent}/${progress.total} queued` : sending ? `Processing ${progress.total} emails...` : ""}
              />
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Text type="secondary">
              {selectedCount} of {totalCount} selected
            </Text>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <Button onClick={onClose} disabled={sending}>Cancel</Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSend}
                loading={sending}
                disabled={selectedCount === 0 || !selectedTemplateId || sent}
              >
                {sending ? "Sending..." : sent ? "Sent!" : `Send to ${selectedCount} claim${selectedCount !== 1 ? "s" : ""}`}
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div style={{ padding: "12px 0" }}>
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 6 }}>Email Template</Text>
          <Select
            placeholder="Select an email template"
            style={{ width: "100%" }}
            options={templateOptions}
            value={selectedTemplateId}
            onChange={setSelectedTemplateId}
            showSearch
            optionFilterProp="label"
            size="large"
          />
        </div>

        <Alert
          type="info"
          showIcon
          message={
            <span>
              Only <Tag color="red" style={{ margin: "0 4px" }}>Not Paid</Tag> claims are shown.
              Claims with delete requests are excluded.
              Duplicates within 24h are automatically skipped.
              Emails are processed in batches of 10.
            </span>
          }
          style={{ marginBottom: 16 }}
        />

        {sent && (
          <Alert
            type="success"
            showIcon
            message={`Bulk email "${selectedTemplateName}" queued for ${selectedCount} claim(s). Processing in background.`}
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            preserveSelectedRowKeys: true,
          }}
          columns={columns}
          dataSource={claims}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `${total} claims` }}
          scroll={{ y: 400 }}
        />
      </div>
    </StyledModal>
  );
};

export default BulkEmailModal;
